import {
  compoundV2Oracles,
  compoundV2OraclesConfig,
  compoundV2TokenArray,
} from '@1delta/data-sdk'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { formatUnits } from 'viem'
import { createMarketUid } from '../../../utils'

/**
 * Compound V2 query metadata
 */
export interface CompoundV2QueryMetaExtended {
  cToken: string
  underlying: string
  lender: string
  /** Base asset override (if lender uses non-USD base) */
  baseAssetOverride?: string
  /** Fixed scaling decimals override (replaces default 36 - underlyingDecimals) */
  scalingDecimalsOverride?: number
}

/**
 * Compound V2 Oracle ABI - getUnderlyingPrice
 */
export const CompoundV2OracleAbi = [
  {
    inputs: [{ internalType: 'address', name: 'cToken', type: 'address' }],
    name: 'getUnderlyingPrice',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

/**
 * Get multicall calls for Compound V2 oracles
 * Uses compoundV2Oracles map and compoundV2TokenArray for cToken data
 * Price is normalized via: price / (10^(18 + 18 - underlyingDecimals))
 */
export function getCompoundV2Calls(
  chainId: string,
): FetcherResult<CompoundV2QueryMetaExtended[]>[] {
  const oracles = compoundV2Oracles() ?? {}
  const tokenArrays = compoundV2TokenArray() ?? {}
  const config = compoundV2OraclesConfig() ?? {}
  const results: FetcherResult<CompoundV2QueryMetaExtended[]>[] = []

  for (const [lender, chainOracles] of Object.entries(oracles)) {
    const oracleAddress = chainOracles[chainId]
    const tokens = tokenArrays[lender]?.[chainId] ?? []

    if (!oracleAddress || tokens.length === 0) continue

    const calls: MulticallCall[] = []
    const meta: CompoundV2QueryMetaExtended[] = []

    const lenderConfig = config[lender]?.[chainId]
    const baseAssetOverride = lenderConfig?.baseAsset
    const scalingDecimalsOverride = lenderConfig?.scalingDecimals

    for (const token of tokens) {
      calls.push({
        address: oracleAddress,
        name: 'getUnderlyingPrice',
        params: [token.cToken],
      })

      meta.push({
        cToken: token.cToken,
        underlying: token.underlying,
        lender,
        baseAssetOverride,
        scalingDecimalsOverride,
      })
    }

    if (calls.length > 0) {
      results.push({
        calls,
        meta,
        lender,
      })
    }
  }

  return results
}

/**
 * Parse Compound V2 oracle results into structured price entries
 * Price is normalized via: price / (10^(18 + 18 - underlyingDecimals))
 * Decimals are read from tokenList
 */
export function parseCompoundV2Results(
  data: any[],
  meta: CompoundV2QueryMetaExtended[],
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId, tokenList, usdPrices } = context
  const entries: OraclePriceEntry[] = []

  data.forEach((rawResult, index) => {
    const queryMeta = meta[index]
    if (!queryMeta || rawResult === undefined || rawResult === '0x') return

    try {
      // Get underlying decimals from tokenList, default to 18
      const underlyingLc = queryMeta.underlying.toLowerCase()
      const underlyingDecimals = tokenList?.[underlyingLc]?.decimals ?? 18

      // Price scaling: price / (10^(36 - underlyingDecimals))
      // Some lenders use a fixed scaling instead (see compound-v2-oracles-config.json)
      const scalingDecimals =
        queryMeta.scalingDecimalsOverride ?? 36 - underlyingDecimals
      const rawPriceNum = Number(
        formatUnits(BigInt(rawResult.toString()), scalingDecimals),
      )

      if (isNaN(rawPriceNum) || rawPriceNum === 0) return

      // For lenders with non-USD base assets (e.g. CREAM_FINANCE on BNB),
      // scale the raw price by the base asset's USD price.
      let baseAssetUSD = 1
      if (queryMeta.baseAssetOverride) {
        const baseAssetLc = queryMeta.baseAssetOverride.toLowerCase()
        const baseOracleKey =
          tokenList?.[baseAssetLc]?.assetGroup ?? `${chainId}-${baseAssetLc}`
        baseAssetUSD = usdPrices[baseOracleKey] ?? usdPrices[baseAssetLc] ?? 1
      }

      const priceUSD = rawPriceNum * baseAssetUSD
      const isBaseAsset =
        queryMeta.baseAssetOverride &&
        underlyingLc === queryMeta.baseAssetOverride.toLowerCase()

      entries.push({
        asset: underlyingLc,
        price: rawPriceNum,
        priceUSD,
        marketUid: createMarketUid(chainId, queryMeta.lender, queryMeta.cToken),
        staticBase: isBaseAsset || undefined,
        baseAsset: queryMeta.baseAssetOverride,
      })
    } catch {
      // Skip invalid prices
    }
  })

  return entries
}

/**
 * Get ABI for Compound V2 oracle calls
 */
export function getCompoundV2Abi() {
  return CompoundV2OracleAbi
}

/**
 * Compound V2 Oracle Fetcher module
 */
export const compoundV2Fetcher = {
  getCalls: getCompoundV2Calls,
  parse: parseCompoundV2Results,
  getAbi: getCompoundV2Abi,
}
